import { type ReactElement, type ReactNode, useLayoutEffect, useRef, useState } from "react";

/**
 * How far a device of `device` px has to shrink to fit `available` px: never enlarged, and
 * unscaled while there is nothing to measure yet.
 */
export const previewScale = (available: number, device: number): number =>
  available > 0 ? Math.min(1, available / device) : 1;

/**
 * A device of `width` px, shrunk to the space it gets instead of cut off. The inner box keeps
 * the device width, so the calendar's container queries pick the device's columns; only the
 * drawing is scaled. Without a ResizeObserver (tests) it stays at full size.
 */
export function ScaledDevice({
  width,
  children,
}: {
  readonly width: number;
  readonly children: ReactNode;
}): ReactElement {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ scale: 1, height: 0 });

  useLayoutEffect(() => {
    const o = outer.current;
    const i = inner.current;
    if (o === null || i === null || typeof ResizeObserver === "undefined") {
      return;
    }
    const measure = (): void => {
      const scale = previewScale(o.clientWidth, width);
      const height = i.offsetHeight * scale;
      setBox((last) => (last.scale === scale && last.height === height ? last : { scale, height }));
    };
    const observer = new ResizeObserver(measure);
    observer.observe(o);
    observer.observe(i);
    measure();
    return () => {
      observer.disconnect();
    };
  }, [width]);

  return (
    <div
      ref={outer}
      className="overflow-hidden [contain:inline-size]"
      style={{ height: box.height > 0 ? box.height : undefined }}
    >
      <div
        ref={inner}
        style={{ width, transform: `scale(${String(box.scale)})`, transformOrigin: "top left" }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * A settings page with its preview beside it: from 80rem the form on the left and the preview
 * on the right, held in view while the form scrolls and short enough to leave room for the
 * save bar under it. Narrower, one column with the preview after the form. The form comes
 * first in the document either way, so keyboard and screen reader reach it before the preview.
 *
 * The scrollbar's room is kept even while nothing overflows: ScaledDevice sizes itself on the
 * aside's width, so a scrollbar that came and went with its height would flicker in a loop.
 */
export function EditorLayout({
  preview,
  previewWide = false,
  children,
}: {
  readonly preview: ReactNode;
  /** The preview is where the editing happens, so it takes the larger column. */
  readonly previewWide?: boolean;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <div
      className={`grid grid-cols-1 gap-6 ${previewWide ? "xl:grid-cols-[minmax(22rem,2fr)_minmax(0,3fr)]" : "xl:grid-cols-[minmax(0,3fr)_minmax(22rem,2fr)]"}`}
    >
      <div className="min-w-0">{children}</div>
      <aside
        aria-label="Vorschau"
        className="min-w-0 self-start rounded-md bg-muted p-3 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-7rem)] xl:overflow-auto xl:[scrollbar-gutter:stable]"
      >
        {preview}
      </aside>
    </div>
  );
}
